import React, { useState, useEffect } from 'react';
import { View, ScrollView } from 'react-native';

import { ColorPicker } from './color-picker';
import { RecurrenceEditor } from './recurrence-editor';

import { DateInput } from '@/components/form/date-input';
import { AssociationTypeahead } from '@/components/search/association-typeahead';
import { UserTypeahead } from '@/components/search/user-typeahead';
import { Text, TextInput, Button, useTheme } from '@/components/ui';
import { useAssociation, useUser } from '@/hooks/use-data';
import { TaskStatus, RecurrenceType, type RecurrenceRule } from '@/types/models';
import type { Task, User, Association } from '@/types/models';
import { dueDayMs, formatBusinessDateIso, parseIsoBusinessDay } from '@/utils/date-utils';

interface TaskFormProps {
	initialData?: Partial<Task>;
	onSubmit: (data: Partial<Task>) => void;
	isLoading?: boolean;
	submitLabel?: string;
}

const STATUS_OPTIONS = [
	{ value: TaskStatus.Open, label: 'Open' },
	{ value: TaskStatus.InProgress, label: 'In Progress' },
	{ value: TaskStatus.Completed, label: 'Completed' },
	{ value: TaskStatus.Cancelled, label: 'Cancelled' },
];

export const TaskForm: React.FC<TaskFormProps> = ({ initialData, onSubmit, isLoading, submitLabel = 'Save' }) => {
	const theme = useTheme();
	const [title, setTitle] = useState(initialData?.title || '');
	const [description, setDescription] = useState(initialData?.description || '');
	const [status, setStatus] = useState(initialData?.status || TaskStatus.Open);
	const [selectedUser, setSelectedUser] = useState<User | null>(null);
	const [selectedAssociation, setSelectedAssociation] = useState<Association | null>(null);
	const [color, setColor] = useState<string | undefined>(initialData?.color ?? undefined);
	const [taskList, setTaskList] = useState(initialData?.taskList || 'work');
	const [isRecurring, setIsRecurring] = useState(initialData?.isRecurring || false);
	const [recurrenceRule, setRecurrenceRule] = useState<RecurrenceRule>(() => ({
		type: RecurrenceType.Daily,
		interval: 1,
	}));
	const [dueDate, setDueDate] = useState(() => {
		const day = dueDayMs(initialData?.dueDate);
		return day === null ? '' : formatBusinessDateIso(day);
	});

	const { data: initialUser } = useUser(initialData?.assignedTo || '');
	const { data: initialAssociation } = useAssociation(initialData?.associationId || '');

	useEffect(() => {
		if (initialUser && !selectedUser) setSelectedUser(initialUser);
	// eslint-disable-next-line react-hooks/exhaustive-deps -- selectedUser is read in condition and set; adding it would cause infinite loop
	}, [initialUser]);

	useEffect(() => {
		if (initialAssociation && !selectedAssociation) setSelectedAssociation(initialAssociation);
	// eslint-disable-next-line react-hooks/exhaustive-deps -- selectedAssociation is read in condition and set; adding it would cause infinite loop
	}, [initialAssociation]);

	const handleSubmit = () => {
		const data: Partial<Task> = {
			title,
			description: description || undefined,
			status,
			color: color || null as any,
			taskList,
			assignedTo: selectedUser?.id || undefined,
			associationId: selectedAssociation?.id || undefined,
			dueDate: parseIsoBusinessDay(dueDate) ?? undefined,
			isRecurring,
			// Note: Recurring task creation would be handled separately
		};
		onSubmit(data);
	};

	return (
		<ScrollView style={{ flex: 1, backgroundColor: theme.colors.background }}>
			<View style={{ padding: 16, gap: 16 }}>
				<TextInput
					label="Title *"
					value={title}
					onChangeText={setTitle}
					mode="outlined"
				/>

				<TextInput
					label="Description"
					value={description}
					onChangeText={setDescription}
					mode="outlined"
					multiline
					numberOfLines={4}
				/>

				<View>
					<Text style={{ fontSize: 12, color: theme.colors.onSurfaceVariant, marginBottom: 4 }}>Status</Text>
					<View style={{ flexDirection: 'row', gap: 8, flexWrap: 'wrap' }}>
						{STATUS_OPTIONS.map((opt) => (
							<Button
								key={opt.value}
								mode={status === opt.value ? 'contained' : 'outlined'}
								onPress={() => setStatus(opt.value)}
								compact
							>
								{opt.label}
							</Button>
						))}
					</View>
				</View>

				<View>
					<Text style={{ fontSize: 12, color: theme.colors.onSurfaceVariant, marginBottom: 4 }}>Task List</Text>
					<View style={{ flexDirection: 'row', gap: 8 }}>
						<Button
							mode={taskList === 'work' ? 'contained' : 'outlined'}
							onPress={() => setTaskList('work')}
							compact
						>
							Work Tasks
						</Button>
						<Button
							mode={taskList === 'personal' ? 'contained' : 'outlined'}
							onPress={() => setTaskList('personal')}
							compact
						>
							Personal Tasks
						</Button>
					</View>
				</View>

				<ColorPicker value={color} onValueChange={setColor} />

				<DateInput
					label="Due Date"
					utcDay
					value={(() => { const m = dueDate.match(/^(\d{4})-(\d{2})-(\d{2})$/); return m ? new Date(`${dueDate}T05:00:00Z`) : null; })()}
					onChange={(d) => { setDueDate(d ? `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}` : ''); }}
				/>

				<View style={{ zIndex: 20 }}>
					<UserTypeahead
						label="Assigned To"
						value={selectedUser}
						onSelect={setSelectedUser}
						placeholder="Search for a user..."
					/>
				</View>

				<View style={{ zIndex: 10 }}>
					<AssociationTypeahead
						label="Association"
						value={selectedAssociation}
						onSelect={setSelectedAssociation}
						placeholder="Search for an association..."
					/>
				</View>

				{/* Recurrence Settings */}
				<View>
					<Text style={{ fontSize: 12, color: theme.colors.onSurfaceVariant, marginBottom: 8 }}>
						Recurrence
					</Text>
					<View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
						<Text style={{ fontSize: 14, color: theme.colors.onSurface }}>
							{isRecurring ? 'Repeats' : 'Does not repeat'}
						</Text>
						<Button
							mode={isRecurring ? 'contained' : 'outlined'}
							onPress={() => setIsRecurring(!isRecurring)}
							compact
						>
							{isRecurring ? 'Edit' : 'Add'}
						</Button>
					</View>
				</View>

				{isRecurring && (
					<RecurrenceEditor
						recurrenceRule={recurrenceRule}
						onChange={setRecurrenceRule}
						disabled={isLoading}
					/>
				)}

				<Button
					mode="contained"
					onPress={handleSubmit}
					loading={isLoading}
					disabled={!title.trim() || isLoading}
					style={{ marginTop: 16 }}
				>
					{submitLabel}
				</Button>
			</View>
		</ScrollView>
	);
};
